"use client"

import { useState } from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useForm } from "react-hook-form"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Input } from "@/components/ui/input"
import { PenIcon, Loader2 } from "lucide-react"
import BusinessService from "@/api/businesses.service";
import {useToast} from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress"
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'

const formSchema = z.object({
  businessType: z.string(),
  name: z.string(),
  description: z.string(),
  address: z.string(),
  city: z.string(),
  state: z.string(),
  postalCode: z.string(),
  country: z.string(),
  emailAddress: z.string().email(),
  phoneNumber: z.string(),
  website: z.string(),
  logoImgLink: z.string().optional(),
  bannerImgLink: z.string().optional(),
})

interface EditBusinessFormProps {
  business: any;
}

export function EditBusinessForm({ business }: EditBusinessFormProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logoImg, setLogoImg] = useState<File | null>(null);
  const [bannerImg, setBannerImg] = useState<File | null>(null);

  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: business,
  })

  const handleUpload = (imageType: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      if (imageType === "logoImg") {
        setLogoImg(e.target.files[0]);
      } else if (imageType === "bannerImg") {
        setBannerImg(e.target.files[0]);
      } else {
        toast({
          title: "Error",
          description: "Please select a valid image",
          duration: 3000,
        });
        return;
      }
    }
  }

  // 2. Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    const newFormData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      newFormData.append(key, value as string);
    });
    if (logoImg) {
      newFormData.append('logoImg', logoImg);
    }
    if (bannerImg) {
      newFormData.append('bannerImg', bannerImg);
    }

    try {
      const response = await BusinessService.updateSpecificBusiness(business.id, newFormData, (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setProgress(percentCompleted);
      });
      const result = response.data;
      if (result.result === "success") {
        toast({
          title: "Success 🎉",
          description: "Business updated successfully. Logging out...",
        });
        setTimeout(() => {
          window.location.href = '/logout';
        }, 3000);
      } else {
        toast({
          title: "Error ❌",
          description: result.message || "Failed to update business",
        });
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error ❌",
        description: `${error}`
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <div className="grid grid-cols-2 gap-4">
          
          <FormField
            control={form.control}
            name="businessType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Type</FormLabel>
                <FormControl>
                  <Select {...field} defaultValue={field.value ?? ''} value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a business type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_proprietorship">Sole Proprietorship</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="corporation">Corporation</SelectItem>
                      <SelectItem value="limited_liability_company">Private Limited Company</SelectItem>
                      <SelectItem value="nonprofit">Non-Profit Organization</SelectItem>
                      <SelectItem value="ngo">Non-Governmental Organization</SelectItem>
                      <SelectItem value="trust">Trust</SelectItem>
                      {/* Add more SelectItem as needed */}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormDescription>
                  Enter the business type.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Business Name" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business name.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea placeholder="Description" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business description.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Input placeholder="123 Main St" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business address.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City</FormLabel>
                <FormControl>
                  <Input placeholder="New York" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business city.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>State</FormLabel>
                <FormControl>
                  <Input placeholder="NY" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business state.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Postal Code</FormLabel>
                <FormControl>
                  <Input placeholder="10001" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business postal code.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Country</FormLabel>
                <FormControl>
                  <Input placeholder="USA" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business country.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="emailAddress"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <Input placeholder="business@example.com" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business email address.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <PhoneInput placeholder="+1234567890" {...field} className="appearance-none px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm" />
                </FormControl>
                <FormDescription>
                  Enter the business phone number.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="website"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Website</FormLabel>
                <FormControl>
                  <Input placeholder="https://example.com" {...field} />
                </FormControl>
                <FormDescription>
                  Enter the business website.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormItem>
            <FormLabel>Logo Image</FormLabel>
            <FormControl>
              <Input type="file" onChange={(e) => handleUpload("logoImg", e)} />
            </FormControl>
            {form.getValues("logoImgLink") && (
              <div className="mt-2">
                <img src={form.getValues("logoImgLink")} alt="Logo Image" className="w-32 h-32 object-cover rounded-lg shadow-md" />
              </div>
            )}
            <FormDescription>
              Upload the business logo image.
            </FormDescription>
            <FormMessage />
          </FormItem>

          <FormItem>
            <FormLabel>Banner Image</FormLabel>
            <FormControl>
              <Input type="file" onChange={(e) => handleUpload("bannerImg", e)} />
            </FormControl>
            {form.getValues("bannerImgLink") && (
              <div className="mt-2">
                <img src={form.getValues("bannerImgLink")} alt="Banner Image" className="w-full h-32 object-cover rounded-lg shadow-md" />
              </div>
            )}
            <FormDescription>
              Upload the business banner image.
            </FormDescription>
            <FormMessage />
          </FormItem>

        </div>
        <div className="flex justify-end">
        { isSubmitting && <Progress value={progress} style={{ backgroundColor: '#0E415E', color: '#96d0f2' }} /> }
          <Button type="submit" className="bg-[#0E415E] hover:bg-[#1F628D] text-white">
            {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating...</> : <><PenIcon className="w-4 h-4 mr-2" /> Update business</>}
          </Button>
        </div>
      </form>
    </Form>
  )
}
